import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Users, DollarSign, Video, TrendingUp } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export default function DashboardPage() {
  const { profile, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !profile) {
      navigate('/login');
    }
  }, [profile, loading, navigate]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) return null;

  const getStatsCards = () => {
    if (profile.role === 'admin') {
      return [
        {
          title: 'Utenti totali',
          value: '1,234',
          description: '+12% rispetto al mese scorso',
          icon: Users,
        },
        {
          title: 'Corsi attivi',
          value: '89',
          description: '+5 questo mese',
          icon: BookOpen,
        },
        {
          title: 'Revenue totale',
          value: '€45,231',
          description: '+20% rispetto al mese scorso',
          icon: DollarSign,
        },
        {
          title: 'Live sessions',
          value: '23',
          description: 'Questa settimana',
          icon: Video,
        },
      ];
    }
    if (profile.role === 'docente') {
      return [
        {
          title: 'I miei corsi',
          value: '8',
          description: '3 pubblicati',
          icon: BookOpen,
        },
        {
          title: 'Studenti totali',
          value: '156',
          description: '+18 questo mese',
          icon: Users,
        },
        {
          title: 'Guadagni',
          value: '€3,240',
          description: 'Questo mese',
          icon: DollarSign,
        },
        {
          title: 'Prossime live',
          value: '4',
          description: 'Questa settimana',
          icon: Video,
        },
      ];
    }
    return [
      {
        title: 'Corsi iscritti',
        value: '5',
        description: '2 completati',
        icon: BookOpen,
      },
      {
        title: 'Ore di video',
        value: '24h',
        description: 'Tempo totale di apprendimento',
        icon: Video,
      },
      {
        title: 'Progressi',
        value: '68%',
        description: 'Media completamento',
        icon: TrendingUp,
      },
      {
        title: 'Live partecipate',
        value: '12',
        description: 'Questo mese',
        icon: Video,
      },
    ];
  };

  const statsCards = getStatsCards();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Benvenuto, {profile.full_name || 'Utente'}!
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 capitalize">
            Dashboard {profile.role}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {statsCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {card.title}
                  </CardTitle>
                  <Icon className="w-4 h-4 text-gray-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{card.value}</div>
                  <p className="text-xs text-gray-600 mt-1">
                    {card.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Attività recente</CardTitle>
              <CardDescription>Ultimi aggiornamenti sulla piattaforma</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 mt-2" />
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium">Attività {i}</p>
                      <p className="text-xs text-gray-600">
                        Descrizione dell'attività recente
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">2h fa</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Prossimi eventi</CardTitle>
              <CardDescription>Live sessions e scadenze</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Video className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium">Live Session {i}</p>
                      <p className="text-xs text-gray-600">
                        Domani alle 15:00
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
